import React from 'react';
import Box from 'Components/Atoms/Box';
import './styles.scss';
import { CurrentStatus, Reservation } from 'types/reservations';
import TabCard from 'Components/Molecules/TabCard';
import ReservationCard from '../ReservationCard';
import ActionBar from './Components/ActionBar';
import { isTouchDevice, toCurrencyString } from 'utils/helper';
import Typography from 'Components/Atoms/Typography';
import { CircularProgress } from '@material-ui/core';
import {
  ReservationActionTypes,
  ReservationContext,
} from 'Contexts/ReservationContext';
import { ReservationShift } from 'types/shared/helper/shifts';
import WaitinglistCard from '../WaitinglistCard';
import ReservationTable, { isPrintShortcut } from '../ReservationTable';
import { TakeAwayOrder } from 'types/takeAway';
import useReservationList from 'CustomHooks/useReservationList';
import useKeyPressListener from 'CustomHooks/useKeyPressListener';
import useExperimentalFlags from 'CustomHooks/useExperimentalFlags';
import ShiftComments from './Components/ShiftComments';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

export type LeftSideBarProps = {
  reservations: Reservation[];
  /** The whole day, so the search also finds guests of another shift. */
  allReservations?: Reservation[];
  restaurantId: string;
  date: string;
  loading?: boolean;
  occassions: { id: string; title: string; hidden?: boolean }[];
  spaces: { id: string; name: string }[];
  currentShift?: ReservationShift | null;
  handleAction: (
    id: string,
    type: ReservationActionTypes,
    payload?: any
  ) => void;
  warnings?: string[];
  active: string | null;
  setActive: (id: string | null) => void;
};

type ReservationSidebarItem =
  | { type: 'same-custom-id'; key: string; reservation: Reservation }
  | { type: 'take-away'; key: string; order: TakeAwayOrder }
  | { type: 'reservation'; key: string; reservation: Reservation }
  | { type: 'empty'; key: string }
  | { type: 'loading'; key: string };

type ReservationListHeaderContext = {
  search: string | null;
  loadingSameCustomId: boolean;
};

const LeftSidebarScroller = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={['left-sidebar-scroll', className].filter(Boolean).join(' ')}
  />
));

LeftSidebarScroller.displayName = 'LeftSidebarScroller';

const VIRTUOSO_COMPONENTS = {
  Scroller: LeftSidebarScroller,
};

const ReservationListHeader: React.FC<{
  context?: ReservationListHeaderContext;
}> = ({ context }) => {
  if (!context) return null;

  return (
    <>
      {context.search && context.loadingSameCustomId && (
        <Box className="center">
          <CircularProgress />
        </Box>
      )}
      <ShiftComments />
    </>
  );
};

const RESERVATION_LIST_COMPONENTS = {
  ...VIRTUOSO_COMPONENTS,
  Header: ReservationListHeader,
};

const LeftSideBar: React.FC<LeftSideBarProps> = ({
  reservations,
  allReservations,
  date,
  loading = false,
  occassions,
  spaces,
  handleAction,
  warnings = [],
  active,
  restaurantId,
  setActive,
  currentShift = null,
}) => {
  // const [upcomingOnly, setupcomingOnly] = useState(false);

  const hidePlacedGuests = !!useExperimentalFlags().data?.hidePlacedGuests;
  const reservationListRef = React.useRef<VirtuosoHandle>(null);
  const waitingListRef = React.useRef<VirtuosoHandle>(null);
  const lastFocusedReservationRef = React.useRef<string | null>(null);
  const lastFocusedWaitingListEntryRef = React.useRef<string | null>(null);

  const props = useReservationList({
    reservations,
    date,
    loading,
    occassions,
    spaces,
    handleAction,
    warnings,
    active,
    restaurantId,
    setActive,
    currentShift,
    allReservations,
  });

  const { tickets } = React.useContext(ReservationContext);
  const [exporting, setexporting] = React.useState(false);

  // The requests tab lists waiting list entries, the export holds reservations
  const showsRequests = props.hasWaitingList && props.tab === 'waitinglist';

  useKeyPressListener(
    (e) => {
      if (!isPrintShortcut(e) || showsRequests) return;
      e.preventDefault();
      if (!e.repeat) setexporting(true);
    },
    { deps: [showsRequests] }
  );

  const warningSet = React.useMemo(() => new Set(warnings), [warnings]);

  const reservationItems = React.useMemo<ReservationSidebarItem[]>(() => {
    const items: ReservationSidebarItem[] = [];
    const keepReservationsAtTheTop =
      !!props.reservationSettings?.keepReservationsAtTheTop;
    const recentStartedCutoff = Date.now() - 5 * 60000;

    if (props.search && !props.loadingSameCustomId) {
      items.push(
        ...props.reservationsWithSameCustomId.map((reservation) => ({
          type: 'same-custom-id' as const,
          key: `same-custom-id-${reservation.id}`,
          reservation,
        }))
      );
    }

    if (props.pendingTakeAwayOrders.data) {
      items.push(
        ...props.pendingTakeAwayOrders.data.map((order) => ({
          type: 'take-away' as const,
          key: `take-away-${order.id}`,
          order,
        }))
      );
    }

    items.push(
      ...props.sortedReservations
        .filter(
          (res) =>
            !['paid', 'hasOrdered'].includes(res.currentStatus) &&
            (res.currentStatus !== CurrentStatus.SEATED ||
              res.partlySeated ||
              (keepReservationsAtTheTop &&
                res.startedAt &&
                res.startedAt > recentStartedCutoff)) &&
            (!hidePlacedGuests || !res.started)
        )
        .map((reservation) => ({
          type: 'reservation' as const,
          key: `reservation-${reservation.id}`,
          reservation,
        }))
    );

    if (!hidePlacedGuests) {
      items.push(
        ...props.sortedReservations
          .filter(
            (res) =>
              ['seated', 'paid', 'hasOrdered'].includes(res.currentStatus) &&
              !res.partlySeated &&
              (!keepReservationsAtTheTop ||
                !res.startedAt ||
                res.startedAt < recentStartedCutoff)
          )
          .map((reservation) => ({
            type: 'reservation' as const,
            key: `reservation-seated-${reservation.id}`,
            reservation,
          }))
      );
    }

    if (!props.sortedReservations.length && !loading) {
      items.push({ type: 'empty', key: 'empty' });
    }

    if (loading) {
      items.push({ type: 'loading', key: 'loading' });
    }

    return items;
  }, [
    props.search,
    props.loadingSameCustomId,
    props.reservationsWithSameCustomId,
    props.pendingTakeAwayOrders.data,
    props.sortedReservations,
    props.reservationSettings?.keepReservationsAtTheTop,
    hidePlacedGuests,
    loading,
  ]);

  React.useEffect(() => {
    if (!active) {
      lastFocusedReservationRef.current = null;
      return;
    }

    if (lastFocusedReservationRef.current === active) return;

    const index = reservationItems.findIndex(
      (item) =>
        (item.type === 'reservation' || item.type === 'same-custom-id') &&
        item.reservation.id === active
    );

    if (index >= 0) {
      lastFocusedReservationRef.current = active;
      reservationListRef.current?.scrollToIndex({
        index,
        align: 'center',
        behavior: isTouchDevice() ? 'auto' : 'smooth',
      });
    }
  }, [active, reservationItems]);

  React.useEffect(() => {
    if (!props.currentWaitinglistEntry) {
      lastFocusedWaitingListEntryRef.current = null;
      return;
    }

    if (
      lastFocusedWaitingListEntryRef.current === props.currentWaitinglistEntry
    )
      return;

    const index = props.filteredWaitingList.findIndex(
      (entry) => entry.id === props.currentWaitinglistEntry
    );

    if (index >= 0) {
      lastFocusedWaitingListEntryRef.current = props.currentWaitinglistEntry;
      waitingListRef.current?.scrollToIndex({
        index,
        align: 'center',
        behavior: isTouchDevice() ? 'auto' : 'smooth',
      });
    }
  }, [props.currentWaitinglistEntry, props.filteredWaitingList]);

  const toggleAllReservations = React.useCallback(
    () => props.setshowAll((x) => !x),
    [props.setshowAll]
  );

  const reservationListContext = React.useMemo<ReservationListHeaderContext>(
    () => ({
      search: props.search,
      loadingSameCustomId: props.loadingSameCustomId,
    }),
    [props.search, props.loadingSameCustomId]
  );

  const renderReservationItem = React.useCallback(
    (_index: number, item: ReservationSidebarItem) => {
      if (item.type === 'same-custom-id') {
        return (
          <ReservationCard
            occassions={occassions}
            reservation={item.reservation}
            active={active === item.reservation.id}
            hasWarnings={warningSet.has(item.reservation.id)}
            hideOccassion={occassions.length <= 1}
            onClick={setActive}
            handleAction={handleAction}
            isAnotherDay
          />
        );
      }

      if (item.type === 'take-away') {
        return <TakeAwayOrderCard order={item.order} />;
      }

      if (item.type === 'reservation') {
        return (
          <ReservationCard
            occassions={occassions}
            reservation={item.reservation}
            active={active === item.reservation.id}
            hasWarnings={warningSet.has(item.reservation.id)}
            hideOccassion={occassions.length <= 1}
            onClick={setActive}
            handleAction={handleAction}
          />
        );
      }

      if (item.type === 'empty') {
        return (
          <Box
            fullSize
            style={{ minHeight: 100, maxHeight: 'calc(100% - 44px)' }}
            className="center"
            padding="md"
          >
            <Typography
              variant="text-3"
              color="subdued"
              translation="reservations"
              textAlign="center"
            >
              No corresponding reservation
            </Typography>
          </Box>
        );
      }

      return (
        <Box
          style={{ flexGrow: 10, minHeight: '100%' }}
          className="center"
          flex
          padding="md"
        >
          <CircularProgress />
        </Box>
      );
    },
    [active, handleAction, occassions, setActive, warningSet]
  );

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
      className={
        props.hasWaitingList
          ? 'left-sidebar-root with-waitinglist'
          : 'left-sidebar-root'
      }
      id="leftSideBar"
    >
      {props.hasWaitingList && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            background: 'var(--color-paper)',
            padding: '12px',
            border: '1px solid var(--color-border)',
            borderBottom: 'none',
          }}
        >
          <Typography
            color={props.tab === 'reservations' ? 'secondary' : 'subdued'}
            variant="text-2"
            weight="medium"
            onClick={() => {
              props.settab('reservations');
              props.setvalue(0);
            }}
            style={{ cursor: 'pointer' }}
          >
            Reservierungen
          </Typography>
          <Typography
            color={props.tab === 'waitinglist' ? 'secondary' : 'subdued'}
            variant="text-2"
            weight="medium"
            onClick={() => {
              props.settab('waitinglist');
              props.setvalue(0);
            }}
            style={{ cursor: 'pointer' }}
          >
            Anfragen ({props.waitinglistCount})
          </Typography>
        </Box>
      )}
      {(props.tab === 'reservations' || !props.hasWaitingList) && (
        <TabCard
          tabs={props.tabs as any}
          id="leftSideBarCard"
          className="left-sidebar"
          value={props.value}
          onChange={props.setvalue}
          hideHeader
          experimentalScroll={false}
          style={{
            borderRadius: 0,
            minHeight: '100%',
            flexGrow: 10,
          }}
          bodyProps={{
            padding: 0,
            className: props.hasWaitingList
              ? 'left-sidebar-body with-waitinglist'
              : 'left-sidebar-body',
            style: { overflow: 'hidden' },
          }}
        >
          <Box
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <ActionBar
              {...{
                ...props,
                toggleAll: toggleAllReservations,
              }}
            />
            <Virtuoso
              ref={reservationListRef}
              style={{ flex: '1 1 auto', minHeight: 0 }}
              data={reservationItems}
              components={RESERVATION_LIST_COMPONENTS}
              context={reservationListContext}
              computeItemKey={(_index, item) => item.key}
              itemContent={renderReservationItem}
              increaseViewportBy={400}
            />
          </Box>
        </TabCard>
      )}
      {props.hasWaitingList && props.tab === 'waitinglist' && (
        <TabCard
          tabs={props.waitinglist_tabs as any}
          id="leftSideBarCard"
          className="left-sidebar"
          value={props.value}
          onChange={props.setvalue}
          hideHeader
          experimentalScroll={false}
          style={{
            borderRadius: 0,
            minHeight: '100%',
            flexGrow: 10,
          }}
          bodyProps={{
            padding: 0,
            className: 'left-sidebar-waitinglist-body',
            style: { overflow: 'hidden' },
          }}
        >
          <Virtuoso
            ref={waitingListRef}
            style={{ height: '100%' }}
            data={props.filteredWaitingList}
            components={VIRTUOSO_COMPONENTS}
            computeItemKey={(_index, entry) => entry.id}
            itemContent={(_index, entry) => (
              <WaitinglistCard
                entry={entry}
                hideOccassion={occassions.length <= 1}
                active={props.currentWaitinglistEntry === entry.id}
                onClick={() => {
                  props.setcurrentWaitinglistEntry(entry.id);
                  props.setcurrentReservation(null);
                }}
              />
            )}
            increaseViewportBy={400}
          />
        </TabCard>
      )}
      {exporting && (
        <ReservationTable
          groups={props.search ? [] : props.groups}
          reservations={props.sortedReservations}
          handleAction={handleAction}
          active={active}
          occassions={occassions}
          warnings={props.warnings}
          onRowClick={setActive}
          spaces={spaces}
          fields={props.filters.columns}
          groupBy={props.search ? 'none' : props.filters.groupBy}
          hideEmptyTables={props.filters.hideEmptyTables}
          mode={
            props.filters.mode !== 'tickets' || tickets.data.length
              ? props.filters.mode
              : 'normal'
          }
          onExported={() => setexporting(false)}
        />
      )}
    </Box>
  );
};

export default LeftSideBar;

const TakeAwayOrderCard: React.FC<{
  order: TakeAwayOrder;
}> = ({ order }) => {
  const nameStr = order?.customer?.name || '';

  return (
    <Box
      underline
      id={order.id}
      elevation={0}
      className={`reservation-card resa-overdue`}
    >
      <Box className="space-between">
        <Typography
          variant="text-4"
          block
          className={'text-white'}
          style={{ marginTop: 4 }}
        >
          Bestellung zur Abholung
        </Typography>
      </Box>
      <Typography
        variant="text-3"
        weight="bold"
        block
        className={'text-white'}
        style={{
          marginTop: 3,
          whiteSpace: nameStr.length < 20 ? 'nowrap' : 'normal',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {nameStr}
      </Typography>
      <Typography
        variant="text-4"
        block
        className={'text-white'}
        style={{ marginTop: 4 }}
      >
        {order.time} • {toCurrencyString(order.total)}
      </Typography>
    </Box>
  );
};
